import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Wallet, Eye, EyeSlash, Key } from "@phosphor-icons/react";
import api from "../lib/api";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Password Recovery States
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [displayedCode, setDisplayedCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [recoveryStep, setRecoveryStep] = useState(1);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success("Bem-vindo de volta!");
      navigate("/");
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 403) {
        toast.error(detail || "Acesso não autorizado. Realize o pagamento primeiro.");
      } else {
        toast.error(detail || "Email ou senha incorretos");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setRecoveryLoading(true);
    
    try {
      const response = await api.post("/auth/forgot-password", { email: forgotEmail });
      setDisplayedCode(response.data.recovery_code);
      setRecoveryStep(2);
      toast.success("Código gerado! Anote-o e digite abaixo.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao gerar código");
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    
    setRecoveryLoading(true);
    
    try {
      await api.post("/auth/reset-password", {
        email: forgotEmail,
        recovery_code: recoveryCode,
        new_password: newPassword
      });
      toast.success("Senha alterada com sucesso! Faça login.");
      setShowForgotPassword(false);
      setRecoveryStep(1);
      setForgotEmail("");
      setRecoveryCode("");
      setDisplayedCode("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setRecoveryLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setRecoveryStep(1);
    setForgotEmail("");
    setRecoveryCode("");
    setDisplayedCode("");
    setNewPassword("");
    setConfirmPassword("");
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1566245877487-a4924343d9fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxicmF6aWxpYW4lMjBidXNpbmVzcyUyMG9mZmljZSUyMG1pbmltYWxpc3R8ZW58MHx8fHwxNzcwNTUxMjQwfDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div className="absolute inset-0 bg-stone-900/60" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="font-heading text-4xl font-bold mb-4">
            Controle suas contas com tranquilidade
          </h2>
          <p className="text-white/80 text-lg">
            Organize, acompanhe e nunca mais perca um vencimento.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background noise-bg">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center">
              <Wallet className="w-6 h-6 text-primary-foreground" weight="fill" />
            </div>
            <span className="font-heading font-extrabold text-2xl tracking-tight text-primary">
              Financeiro
            </span>
          </div>

          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
            Entrar na conta
          </h1>
          <p className="text-muted-foreground mb-8">
            Digite suas credenciais para acessar
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base font-medium tracing-beam"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? "Processando..." : "Entrar"}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-primary hover:underline transition-colors"
              data-testid="forgot-password-link"
            >
              Esqueceu a senha?
            </button>
          </div>

          <div className="mt-6 text-center space-y-2">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-sm text-muted-foreground hover:text-primary transition-colors block w-full"
              data-testid="buy-access-link"
            >
              Não tem conta? Comprar acesso
            </button>
            <button
              type="button"
              onClick={() => navigate('/checkout/success')}
              className="text-sm text-muted-foreground hover:text-primary transition-colors block w-full"
              data-testid="already-paid-link"
            >
              Já paguei, quero ativar minha conta
            </button>
          </div>
        </div>
      </div>

      {/* Password Recovery Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={closeForgotPassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Key size={24} className="text-primary" />
              Recuperar Senha
            </DialogTitle>
          </DialogHeader>

          {recoveryStep === 1 ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label>Email da conta</Label>
                <Input
                  type="email"
                  placeholder="seu@email.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                Digite seu email e clique em "Gerar Código". O código aparecerá na tela.
              </p>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeForgotPassword}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={recoveryLoading} data-testid="generate-code-button">
                  {recoveryLoading ? "Gerando..." : "Gerar Código"}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              {/* Display the generated code prominently */}
              <div className="p-4 bg-primary/10 rounded-lg border-2 border-primary text-center">
                <p className="text-sm text-muted-foreground mb-2">Seu código de recuperação:</p>
                <p className="font-mono text-4xl font-bold text-primary tracking-widest" data-testid="displayed-recovery-code">
                  {displayedCode}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Válido por 10 minutos. Anote e digite abaixo.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Digite o código</Label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  maxLength={6}
                  className="text-center font-mono text-xl tracking-widest"
                  required
                  data-testid="recovery-code-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Nova senha</Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={6}
                  required
                  data-testid="new-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Confirmar nova senha</Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={6}
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setRecoveryStep(1)}>
                  Voltar
                </Button>
                <Button type="submit" disabled={recoveryLoading} data-testid="reset-password-button">
                  {recoveryLoading ? "Alterando..." : "Alterar Senha"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
